// orphaned-ticket-recovery-applier.ts
//
// Split out of orphaned-ticket-reconciler.ts (grew past the 200-line budget
// once the binding_corrupt fail-closed path — review 260909-p6-m2 INFO — was
// added on top of the original Bug C reclaim logic). Owns APPLYING a batch of
// `detectOrphanedInProgressTickets` recoveries; detection itself stays in
// orphaned-ticket-reconciler.ts. Re-exported from there so callers keep a
// single import path.
import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { archiveAttemptForensics } from "./attempt-forensics.js";
import type { MissionCommitInput, MissionStore } from "./mission-store.js";
import { bindingPathFor, type OrphanedTicketRecovery } from "./orphaned-ticket-reconciler.js";
import {
  readAttemptStateFile,
  retryStateFor,
  withRetryState,
  writeAttemptStateFile,
} from "./supervisor-attempt-state.js";
// Req 9 (NEW-3): the marker's job ends once its evidence has been folded
// into a committed `attempt_recovered` — never reused for a LATER attempt
// on the same (or another) ticket.
import { deleteUnlockKilledWorkerMarker } from "./unlock-killed-worker-marker.js";

/**
 * Removes a stale binding file a killed attempt left behind (the happy path
 * — `binding-rehash-and-cleanup.ts` — never ran for it). Left in place, the
 * ticket's retry would fail result-verifier.ts's scope check: the leftover
 * file already exists at the retry's baseline snapshot, so the retry's OWN
 * binding write (session-runner.ts always writes one before spawn) reads as
 * a "touched .claude/state/autopilot-binding.json outside allowed_paths"
 * diff — a false scope violation, not a real one. Chmod 0444 (write-once
 * writer intent) means a plain unlink, not overwrite, is required; missing
 * file is a safe no-op (the `supervisor_crash` case never wrote one).
 */
export async function cleanupStaleOrphanedBinding(missionWorkspace: string): Promise<void> {
  await fs.rm(bindingPathFor(missionWorkspace), { force: true });
}

async function applyReclaimableRecoveries(
  store: MissionStore,
  controlRoot: string,
  missionWorkspace: string,
  reclaimable: OrphanedTicketRecovery[],
): Promise<void> {
  if (reclaimable.length === 0) return;
  await cleanupStaleOrphanedBinding(missionWorkspace);

  const attemptState = await readAttemptStateFile(controlRoot);
  let nextAttemptState = attemptState;
  for (const r of reclaimable) {
    const prior = retryStateFor(nextAttemptState, r.ticketId);
    nextAttemptState = withRetryState(nextAttemptState, r.ticketId, {
      ...prior,
      physical_runs: prior.physical_runs + 1,
    });
  }
  await writeAttemptStateFile(controlRoot, nextAttemptState);

  for (const r of reclaimable) {
    const events: MissionCommitInput[] = [
      {
        event_id: randomUUID(),
        delta: { kind: "ticket_state_changed", ticket: r.ticketId, status: "todo" },
      },
      {
        event_id: randomUUID(),
        delta: {
          kind: "attempt_recovered",
          ticket: r.ticketId,
          reason: r.reason,
          evidence: r.evidence,
        },
      },
    ];
    await store.commit(events);
  }
}

async function applyCorruptRecoveries(
  store: MissionStore,
  missionWorkspace: string,
  corrupt: OrphanedTicketRecovery[],
): Promise<void> {
  for (const r of corrupt) {
    // Best-effort — a raw file read is the smallest possible surface for
    // archiving "what the corrupt binding actually contained", and forensics
    // archival never depends on this succeeding (same posture as every other
    // forensics call site in this codebase).
    const rawBindingContents = await fs
      .readFile(bindingPathFor(missionWorkspace), "utf8")
      .catch(() => undefined);
    await archiveAttemptForensics({
      controlRoot: missionWorkspace,
      repoDir: missionWorkspace,
      attemptId: `${r.ticketId}-orphan-reconcile`,
      reason: `contract_error: binding_corrupt — ${r.evidence}`,
      bindingEvidence: { raw_binding_contents: rawBindingContents ?? "(không đọc được)" },
    }).catch(() => undefined);

    const events: MissionCommitInput[] = [
      {
        event_id: randomUUID(),
        delta: { kind: "ticket_state_changed", ticket: r.ticketId, status: "blocked" },
      },
      {
        event_id: randomUUID(),
        delta: {
          kind: "attempt_recovered",
          ticket: r.ticketId,
          reason: r.reason,
          evidence: r.evidence,
        },
      },
    ];
    await store.commit(events);
  }
}

/**
 * Applies a batch of `detectOrphanedInProgressTickets` recoveries: cleans up
 * the stale binding, bumps `physical_runs` in attempt-state.json (§6.4: a
 * physical run happened even though it never finished — counting it keeps
 * `attemptNo` on the retry from colliding with the killed attempt's own
 * `attempt_started{attemptNo}` record), and journals `ticket_state_changed`
 * (-> "todo") + `attempt_recovered`. Deliberately does NOT touch
 * `counted_failures` — a supervisor crash is not evidence the WORKER did
 * anything wrong (§6.4), so it must never count toward `max_counted_failures`
 * the way a real failed attempt does.
 *
 * `binding_corrupt` is the ONE exception to all of the above (review
 * 260909-p6-m2 INFO): the file's true state is unknown, so this is NOT a
 * reclaim — no binding cleanup, no `physical_runs` bump, and the ticket goes
 * to "blocked" (not back to "todo", never auto-retried) with the corrupt
 * file's contents archived as forensics evidence for manual investigation.
 */
export async function applyOrphanedTicketRecoveries(
  store: MissionStore,
  controlRoot: string,
  missionWorkspace: string,
  recoveries: OrphanedTicketRecovery[],
): Promise<void> {
  if (recoveries.length === 0) return;

  const corrupt = recoveries.filter((r) => r.reason === "binding_corrupt");
  const reclaimable = recoveries.filter((r) => r.reason !== "binding_corrupt");

  await applyReclaimableRecoveries(store, controlRoot, missionWorkspace, reclaimable);
  await applyCorruptRecoveries(store, missionWorkspace, corrupt);

  // Req 9 (NEW-3): only once a `supervisor_crash` recovery's evidence has
  // actually been committed (`attempt_recovered`, just above) does the
  // marker's job end — deleting it earlier could lose the distinction for a
  // recovery that failed to commit.
  if (reclaimable.some((r) => r.reason === "supervisor_crash")) {
    await deleteUnlockKilledWorkerMarker(controlRoot);
  }
}
